import { FormControl, Input as MuiInput } from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import { FieldError } from 'react-hook-form/dist/types';
import { withFormError, withFormRef } from '../form-integration';
import ErrorMessage from '../error/error-message';
import InputLabel from '../input-label/input-label';
import { PopoverTooltip } from '../tooltip/tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import HelpIcon from '@material-ui/icons/Help';
import useInputStyles from './form-input.styles';

export interface InputProps {
    label: string;
    isMask?: boolean;
    isDisplayLabel?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    error?: FieldError;
    innerRef?: React.Ref<any>;
    isRequired?: boolean;
    min?: number;
    max?: number;
    value?: string;
    removeicon?: () => void;
    removeicontop?: boolean;
    classes?: { multiline: string; input: string };
    name?: string;
    id?: string;
    ref?: any;
    disabled?: boolean;
    className?: string;
    multiline?: boolean;
    rows?: number;
    setValue: any;
    onChange?: (event: React.ChangeEvent) => void;
    onBlur?: (event: React.ChangeEvent) => void;
    onFocus?: (event: React.ChangeEvent) => void;
    fieldDescription?: string;
    fieldExternalLinkUrl?: string;
    fieldExternalLinkText?: string;
    debouncetime?: number;
    unregister?: () => void
}

const Input: React.FC<InputProps> = ({
    label,
    isMask,
    isDisplayLabel = true,
    hasError,
    errorMessage,
    error,
    ref,
    innerRef,
    isRequired,
    min,
    max,
    removeicon,
    removeicontop,
    id,
    value,
    setValue,
    onBlur = (): void => { },
    onChange,
    fieldDescription,
    fieldExternalLinkUrl,
    fieldExternalLinkText,
    ...props
}) => {

    const classes = useInputStyles();
    const [showPII, setShowPII] = useState(false);
    const [type, setType] = useState('password');
    const [current, setCurrent] = useState(value ? value : '');
    const [masked, setMasked] = useState(new Array(value ? value.length+1 : 1).join('•'));
    
    const handleChange = (event) => {
        if (!isMask) {
            setValue(event.target.name, event.target.value, { shouldValidate: true });
        }
        if (onChange) 
            onChange(event);
    };

    const handleKeyDown = (event) => {
        setType('text');
        var code = event.which;
        if (code === 8) {
            setValue(event.target.name, current.slice(0, -1), { shouldValidate: true });
            setCurrent(current.slice(0, -1));
            if(isMask)
                setMasked(masked.slice(0, -1));
        } else {
            var character = event.key;
            if(character.length === 1)
            {
                setValue(event.target.name, current + character, { shouldValidate: true });
                setCurrent(current + character);
                if(isMask)
                    setMasked(new Array(masked.length+1).join('•') + character);
            }
        }
    }

    useEffect(() => {
        if(!showPII){
            const timer = setTimeout(() => {
                setType('password');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [masked]);

    const handleClickShow = () => {
        if (showPII) setType('password');
        else setType('text');
        setShowPII(!showPII);
    };
    
    const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <FormControl variant="outlined" fullWidth className={classes.root}>
            { isDisplayLabel && 
                (<InputLabel
                    fieldName={label}
                    fieldDescription={null}
                    fieldExternalLinkUrl={fieldExternalLinkUrl}
                    fieldExternalLinkText={fieldExternalLinkText}
                    isRequired={isRequired}
                    hasError={hasError}
                />) 
            }
            {isMask ? 
                <MuiInput
                    {...props}
                    {...(innerRef && { inputRef: innerRef })}
                    fullWidth
                    disableUnderline
                    error={hasError}
                    inputProps={{ minLength: min, maxLength: max, autoComplete: 'disabled' }}
                    id={id}
                    type={type}
                    autoComplete="off"
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    value={showPII || type==='password' ? current : masked}
                    endAdornment={
                        <InputAdornment position="end">
                            <div className={classes.endAdornmentWrapper}>
                                <IconButton
                                    aria-label="toggle visibility"
                                    onClick={handleClickShow}
                                    onMouseDown={handleMouseDown}
                                    edge="end"
                                >
                                    {showPII ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </div>
                        </InputAdornment>}
                /> : 
                <MuiInput
                    {...props}
                    {...(innerRef && { inputRef: innerRef })}
                    fullWidth
                    disableUnderline
                    error={hasError}
                    inputProps={{ minLength: min, maxLength: max, autoComplete: 'disabled' }}
                    id={id}
                    autoComplete="off"
                    onChange={handleChange}
                    defaultValue={value}
                    endAdornment={isDisplayLabel && fieldDescription && <div className={classes.endAdornmentWrapper}><PopoverTooltip messageDescription={fieldDescription}><HelpIcon /></PopoverTooltip></div>}
                />}
            {hasError ? <ErrorMessage message={errorMessage} /> : null}
        </FormControl>
    );
};

export default withFormRef(withFormError(Input)) as React.FC<InputProps>;
export { Input as BaseInput };